import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { Gtm } from '@/domains/core/tracking/utils/Gtm';

const sidePanels = {
  description: 'sidePanel-description',
  economicOperator: 'sidePanel-economicOperator',
  concreteGuarantee: 'sidePanel-concreteGuarantee',
  review: 'sidePanel-review',
  ranking: 'sidePanel-ranking',
  deliveryAndReturns: 'sidePanel-deliveryAndReturns',
};

interface SidePanels {
  concreteGuarantee: string;
  deliveryAndReturns: string;
  description: string;
  economicOperator: string;
  ranking: string;
  review: string;
}

interface Return {
  closeSidePanel: () => void;
  goBack: () => void;
  goToConcreteGuarantee: () => void;
  goToDescription: () => void;
  goToDescriptionManufacturerInfo: () => void;
  goToDeliveryAndReturns: () => void;
  goToReview: () => void;
  hash: string;
  isSidePanelOpened: boolean;
  sidePanels: SidePanels;
}
const trackSection = (
  sectionName:
    | 'description'
    | 'review'
    | 'concreteGuarantee'
    | 'deliveryAndReturns',
) => {
  Gtm.push({
    event: 'interaction_click on open section',
    amp_event_name: 'Click on open section',
    section_name: sectionName,
    page_type: 'product',
  });
};

export const useSidePanel = (): Return => {
  const { asPath, push, back, replace, beforePopState } = useRouter();
  const [pdpPath, hash] = asPath.split('#');

  /*
   * Workaround for preventing scroll on back navigation
   * JIRA: SMAR-1635
   * Known issue in nextjs: https://github.com/vercel/next.js/issues/20951
   */
  useEffect(() => {
    beforePopState((state) => {
      state.options.scroll = false;
      return true;
    });
  }, [beforePopState]);

  const closeSidePanel = () =>
    replace(pdpPath, undefined, { shallow: true, scroll: false });

  const isSidePanelOpened = hash?.includes('sidePanel-');

  const pushHash = (hash: string) => {
    push(`#${hash}`, undefined, {
      shallow: true,
      scroll: false,
    });
  };

  const goToDescription = () => {
    pushHash(sidePanels.description);
    trackSection('description');
  };

  const goToDescriptionManufacturerInfo = () =>
    pushHash(sidePanels.economicOperator);

  const goToReview = () => {
    pushHash(sidePanels.review);
    trackSection('review');
  };
  const goBack = () => back();
  const goToConcreteGuarantee = () => {
    pushHash(sidePanels.concreteGuarantee);
    trackSection('concreteGuarantee');
  };
  const goToDeliveryAndReturns = () => {
    pushHash(sidePanels.deliveryAndReturns);
    trackSection('deliveryAndReturns');
  };

  return {
    closeSidePanel,
    goBack,
    goToConcreteGuarantee,
    goToDescription,
    goToDescriptionManufacturerInfo,
    goToDeliveryAndReturns,
    goToReview,
    hash,
    isSidePanelOpened,
    sidePanels,
  };
};
